import { Smile, X } from 'lucide-react-native';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { AnchoredPopover } from '../../components/AnchoredPopover';
import { Button } from '../../components/Button/Button';
import { Input } from '../../components/Input/Input';
import { Modal } from '../../components/Modal';
import {
  ComposerEmojiOverlay,
  type ComposerEmojiOverlayItem,
} from '../../chat/composer/ComposerEmojiOverlay';
import { EMOJI_CATALOG, filterEmojiCatalog } from '../../chat/composer/emojiCatalog';
import { tokens } from '../../design-system/tokens';
import { useColorScheme } from '../../foundations/colorScheme';
import { CustomDurationPicker } from './CustomDurationPicker';
import { StatusVariationPreview } from './StatusVariationPreview';
import { setStatusStrings } from './strings';
import { STATUS_TEXT_SOFT_CAP, type UserStatus } from './types';
import { useInterval } from './useInterval';
import { useSetStatusViewModel } from './useSetStatusViewModel';

export interface SetStatusModalProps {
  readonly visible: boolean;
  readonly initial?: UserStatus;
  readonly onCommit: (status: UserStatus) => void;
  readonly onClear: (cleared: UserStatus) => void;
  readonly onClose: () => void;
}

// Uniform section label — mirrors the shared `Input` label exactly
// (footnote / weight-500 / primary), so every section in the modal reads
// the same.
const SECTION_LABEL =
  'font-sans text-[13px] leading-[18px] font-medium text-light-text-primary dark:text-dark-text-primary';

export const SetStatusModal: React.FC<SetStatusModalProps> = ({
  visible,
  initial,
  onCommit,
  onClear,
  onClose,
}) => {
  const scheme = useColorScheme();
  const colors = tokens.color.semantic[scheme === 'dark' ? 'dark' : 'light'];

  const vm = useSetStatusViewModel(initial ? { initial } : {});

  // Emoji picker popover (the composer's rich overlay).
  const emojiAnchorRef = useRef<unknown>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiQuery, setEmojiQuery] = useState('');
  const emojiItems = useMemo<readonly ComposerEmojiOverlayItem[]>(
    () =>
      filterEmojiCatalog(EMOJI_CATALOG, emojiQuery).map((entry) => ({
        glyph: entry.code,
        shortcode: entry.name,
        group: entry.group,
      })),
    [emojiQuery],
  );
  const handleEmojiSelect = useCallback(
    (item: ComposerEmojiOverlayItem) => {
      vm.setEmoji(item.glyph);
      setEmojiOpen(false);
      setEmojiQuery('');
    },
    [vm],
  );

  const [openedAtPerf, setOpenedAtPerf] = useState<number | null>(null);
  const [openedAtWall, setOpenedAtWall] = useState<Date | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    if (visible) {
      const perfNow =
        typeof performance !== 'undefined' && typeof performance.now === 'function'
          ? performance.now()
          : Date.now();
      setOpenedAtPerf(perfNow);
      setOpenedAtWall(new Date());
      setElapsedMs(0);
    } else {
      setOpenedAtPerf(null);
      setOpenedAtWall(null);
      setEmojiOpen(false);
    }
  }, [visible]);

  useInterval(
    () => {
      if (openedAtPerf !== null) {
        const perfNow =
          typeof performance !== 'undefined' && typeof performance.now === 'function'
            ? performance.now()
            : Date.now();
        setElapsedMs(perfNow - openedAtPerf);
      }
    },
    1000,
    visible && openedAtPerf !== null,
  );

  const previewNow = useMemo(() => {
    if (!openedAtWall) return undefined;
    return new Date(openedAtWall.getTime() + elapsedMs);
  }, [openedAtWall, elapsedMs]);

  const handleCommit = useCallback(() => {
    onCommit(vm.commit());
    onClose();
  }, [onClose, onCommit, vm]);

  const handleClear = useCallback(() => {
    onClear(vm.clear());
    onClose();
  }, [onClear, onClose, vm]);

  return (
    <Modal visible={visible} onClose={onClose} accessibilityLabel={setStatusStrings.title}>
      <View testID="set-status-modal" className="w-[360px] max-w-full gap-5">
        {/* Header — title + minimal close affordance */}
        <View className="flex flex-row items-center justify-between">
          <Text className="font-sans text-[17px] font-semibold text-light-text-primary dark:text-dark-text-primary">
            {setStatusStrings.title}
          </Text>
          <Pressable
            testID="status-cancel"
            accessibilityRole="button"
            accessibilityLabel={setStatusStrings.cancel}
            onPress={onClose}
            className="h-8 w-8 items-center justify-center rounded-full bg-transparent hover:bg-light-surface-secondary dark:hover:bg-dark-surface-elevated"
          >
            <X size={18} strokeWidth={2} color={colors.icon.muted} />
          </Pressable>
        </View>

        {/* Status — emoji picker trigger + status text, one labelled section */}
        <View className="gap-xs">
          <Text className={SECTION_LABEL}>{setStatusStrings.textLabel}</Text>
          <View className="flex flex-row items-center gap-2">
            <Pressable
              ref={emojiAnchorRef as React.Ref<View>}
              testID="status-emoji-input"
              accessibilityRole="button"
              accessibilityLabel={setStatusStrings.emojiLabel}
              accessibilityState={{ expanded: emojiOpen }}
              onPress={() => setEmojiOpen((o) => !o)}
              className="h-[38px] w-[46px] items-center justify-center rounded-[14px] border"
              style={{
                backgroundColor: colors.surface.secondary,
                borderColor: emojiOpen ? colors.accent.indigo : colors.border.primary,
              }}
            >
              {vm.emoji ? (
                <Text className="text-[20px] leading-[24px]">{vm.emoji}</Text>
              ) : (
                <Smile size={18} strokeWidth={2} color={colors.text.tertiary} />
              )}
            </Pressable>

            <View className="flex-1">
              <Input
                testID="status-text-input"
                accessibilityLabel={setStatusStrings.textLabel}
                placeholder={setStatusStrings.textPlaceholder}
                value={vm.text}
                onChangeText={vm.setText}
                maxLength={STATUS_TEXT_SOFT_CAP}
                fullWidth
              />
            </View>
          </View>
        </View>

        <CustomDurationPicker
          preset={vm.preset}
          customExpiry={vm.customExpiry}
          now={previewNow ?? new Date()}
          onChangePreset={vm.setPreset}
          onChangeCustomExpiry={vm.setCustomExpiry}
        />

        <StatusVariationPreview status={vm.preview} />

        {/* Footer — quiet clear on the left, primary commit on the right */}
        <View className="flex flex-row items-center justify-between pt-1">
          <Button testID="status-clear" variant="text" onPress={handleClear}>
            {setStatusStrings.clear}
          </Button>
          <Button
            testID="status-commit"
            variant="primary"
            disabled={!vm.canCommit}
            onPress={handleCommit}
          >
            {setStatusStrings.commit}
          </Button>
        </View>
      </View>

      {/* Composer emoji picker — opens from the emoji trigger. */}
      <AnchoredPopover
        anchorRef={emojiAnchorRef}
        open={emojiOpen}
        onClose={() => setEmojiOpen(false)}
        placement="bottom"
        align="start"
        gap={8}
      >
        <ComposerEmojiOverlay
          query={emojiQuery}
          items={emojiItems}
          onChangeQuery={setEmojiQuery}
          onSelect={handleEmojiSelect}
          onClose={() => setEmojiOpen(false)}
        />
      </AnchoredPopover>
    </Modal>
  );
};
